import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ComprovacaoBoletim {
  boletim_id: string;
  contrato_id: string;
  numero_boletim: number;
  status_boletim: string;
  aprovado_em: string | null;
  valor_aprovado: number;
  valor_comprovado: number;
  total_evidencias: number;
  ultima_evidencia_em: string | null;
  sla_dias: number;
  cobertura_pct: number;
  dias_atraso: number | null;
  status_cobertura: 'sem_valor' | 'totalmente_comprovado' | 'parcialmente_comprovado' | 'aguardando_comprovacao';
}

/** Lê single source of truth: vw_net_comprovacao_boletim */
export function useComprovacaoBoletim(boletimId?: string) {
  return useQuery({
    queryKey: ['comprovacao-boletim', boletimId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('vw_net_comprovacao_boletim')
        .select('*')
        .eq('boletim_id', boletimId!)
        .maybeSingle();
      if (error) throw error;
      return (data as ComprovacaoBoletim | null) ?? null;
    },
    enabled: !!boletimId,
  });
}

/** Garante 1 faturamento por boletim de forma idempotente (RPC com advisory lock) */
export function useEnsureFaturamento() {
  return useMutation({
    mutationFn: async (boletimId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('net_ensure_faturamento' as any, {
        p_boletim_id: boletimId,
      });
      if (error) throw error;
      return data as string;
    },
  });
}

/** Hook auxiliar para invalidar caches após upload de evidência */
export function useInvalidateComprovacao() {
  const qc = useQueryClient();
  return (boletimId: string) => {
    qc.invalidateQueries({ queryKey: ['comprovacao-boletim', boletimId] });
    qc.invalidateQueries({ queryKey: ['net-pagamentos-evidencias'] });
    qc.invalidateQueries({ queryKey: ['net-faturamentos'] });
  };
}
